<script setup lang="ts">
import { navigationItems, socialLinks, socialIconPaths } from '../../data/navigation'

defineProps<{ isOpen: boolean }>()
const emit = defineEmits<{ close: [] }>()
</script>

<template>
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="isOpen" class="scrim" @click="emit('close')" aria-hidden="true"></div>
    </Transition>

    <Transition name="sheet">
      <nav
        v-if="isOpen"
        class="sheet"
        role="navigation"
        aria-label="Mobile navigation"
      >
        <div class="sheet-head">
          <span class="eyebrow">Menu</span>
          <button class="close" aria-label="Close menu" @click="emit('close')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
              <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
            </svg>
          </button>
        </div>

        <a
          v-for="item in navigationItems"
          :key="item.routeName"
          :href="item.href"
          class="item display display-3"
          @click="emit('close')"
        >
          {{ item.name }}
        </a>

        <div class="socials">
          <a
            v-for="social in socialLinks"
            :key="social.name"
            :href="social.href"
            :aria-label="social.ariaLabel"
            :target="social.href.startsWith('http') ? '_blank' : undefined"
            :rel="social.href.startsWith('http') ? 'noopener noreferrer' : undefined"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path :d="socialIconPaths[social.icon]" />
            </svg>
          </a>
        </div>
      </nav>
    </Transition>
  </Teleport>
</template>

<style scoped>
.scrim {
  position: fixed;
  inset: 0;
  z-index: 70;
  background: rgb(10 9 6 / 0.45);
  backdrop-filter: blur(2px);
}

.sheet {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 75;
  width: min(20rem, 86vw);
  padding: 1.1rem var(--gutter) 2rem;
  background: var(--c-surface);
  border-left: 1px solid var(--c-rule);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.sheet-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 1.4rem;
  border-bottom: 1px solid var(--c-rule);
}
.close {
  width: 2.2rem;
  height: 2.2rem;
  display: grid;
  place-items: center;
  background: none;
  border: 1px solid var(--c-rule);
  border-radius: 50%;
  color: var(--c-ink-2);
  cursor: pointer;
}
.close svg {
  width: 1rem;
  height: 1rem;
}
.close:hover {
  color: var(--c-accent);
  border-color: var(--c-accent);
}

.item {
  display: block;
  padding: 1.1rem 0;
  border-bottom: 1px solid var(--c-rule);
  color: var(--c-ink);
  transition: color 0.2s ease, padding-left 0.3s var(--ease-out-quint);
}
.item:hover {
  color: var(--c-accent);
  padding-left: 0.4rem;
}

.socials {
  display: flex;
  gap: 1.4rem;
  margin-top: auto;
  padding-top: 2rem;
}
.socials a {
  color: var(--c-ink-3);
  transition: color 0.2s ease;
}
.socials a:hover {
  color: var(--c-accent);
}
.socials svg {
  width: 1.15rem;
  height: 1.15rem;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.28s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.sheet-enter-active,
.sheet-leave-active {
  transition: translate 0.36s var(--ease-out-quint);
}
.sheet-enter-from,
.sheet-leave-to {
  translate: 100% 0;
}
</style>
